/* Verbatim port of the §1 "Has this moved?" mention-velocity mini-analysis
 * from service/review/index.html: MV_STATE (~2098), renderTrend
 * (~2102-2127), mvLoad (~2129-2147), mvAnalyze (~2150-2199), mvRender
 * (~2201-2260), mvRenderStatic (~2263-2295).
 *
 * PARAMETERIZED (rule a): renderTrend/mvLoad/mvRenderStatic read the global
 * `STATE` — each takes `state: AppPayloadState` as an explicit parameter.
 * mvAnalyze is pure over its `polls` argument and needs no state. mvRender
 * doesn't read STATE either, but now takes `state` too (appended after its
 * original `d` param) purely to pass through to openResultPopover (ui/
 * popover.ts), whose own signature gained the same explicit-state param —
 * the movers-list click handler needs it.
 *
 * NOT re-ported: `mvNorm` (~2099, `const mvNorm = u => (u||'').toLowerCase()
 * .replace(...)`). Per the F2b brief, this is exactly F1's urlNorm(x,
 * {mode:'full'}) (hub/src/lib/format.ts) — F1 proved the two bodies
 * equivalent against 770 fixture URLs before unifying them. Every mvNorm(x)
 * call site below becomes urlNorm(x, {mode:'full'}).
 *
 * mvLoad's `/impact/timeline` fetch goes through api/impact.ts's
 * fetchTimeline() (F2a) with the exact params the old call site built:
 * brand AS-IS (not stripped, unlike loadTimeline/openResultPopover),
 * max_polls '12'.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * The mvTerm/mvLoc `<select>`s are rendered by views/search.ts's own
 * template (same cross-module-ownership shape as timeline.ts's tlLoc/
 * tlStart/tlEnd) — renderTrend only populates their `<option>` lists
 * (pure template-building, converted to render()) and wires onchange
 * imperatively afterward, same carve-out as views/timeline.ts's
 * wireImpControls. #trendMeta/#trendChart/#trendTakeaways/#moversList,
 * by contrast, are fully owned end-to-end by this file (both their markup
 * AND their behavior) — those convert completely, including the
 * movers-list row click (`.mv-r`), which becomes `@click`.
 */
import { captureSerp, isSerpCurrent, serpRoot } from './mountRoot.ts';
import { html, nothing, render, type TemplateResult } from 'lit';
import { fetchTimeline, type TimelinePoll, type TimelineResponse } from '../api/impact.ts';
import { ownershipByTermLoc } from '../derive/search.ts';
import { domainOf, faviconUrl, fixMojibake, pct, urlNorm } from '../lib/format.ts';
import { openResultPopover } from '../ui/popover.ts';
import { chartCaption, chartTableTwin, percentLineLayout, percentLineSvg } from '../ui/charts.ts';
import { chartExportBar } from '../ui/chartExport.ts';
import { IMP_FEATURE_LABEL } from './timeline.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { SerpResult } from '../types/contract.ts';

/** One entered/dropped/moved page-1 result in the movement story. */
interface MvMove extends SerpResult {
  status: string;
  firstPos: number | null;
  lastPos: number | null;
  delta?: number;
}

/** One point of the SoV series (from pollsToSeries or the baked digest). */
interface SovPoint {
  date: string;
  owned: number;
  negative: number;
  slots: number;
}

interface MvState {
  term: string | null;
  location: string | null;
  start: string | null;
  end: string | null;
  data: (TimelineResponse & { _key?: string }) | null;
}
export const MV_STATE: MvState = { term:null, location:null, start:null, end:null, data:null };

export function renderTrend(state: AppPayloadState): void {
  const rows = ownershipByTermLoc(state);
  const tr = state.impact && state.impact.trends;
  const win: { start?: string; end?: string } = (state.impact && state.impact.window) || {};
  const terms = [...new Set(rows.map(r => r.term))];
  if (!MV_STATE.term || !terms.includes(MV_STATE.term))
    MV_STATE.term = (tr && tr.primaryTerm && terms.includes(tr.primaryTerm)) ? tr.primaryTerm : terms[0] ?? null;
  const locs = [...new Set(rows.filter(r => r.term === MV_STATE.term).map(r => r.location))];
  if (!MV_STATE.location || !locs.includes(MV_STATE.location))
    MV_STATE.location = (tr && tr.location && locs.includes(tr.location)) ? tr.location : locs[0] ?? null;
  if (!MV_STATE.start) MV_STATE.start = win.start || '';
  if (!MV_STATE.end)   MV_STATE.end   = win.end || '';

  const tSel = serpRoot().querySelector<HTMLElement>('#mvTerm') as HTMLSelectElement | null, lSel = serpRoot().querySelector<HTMLElement>('#mvLoc') as HTMLSelectElement | null;
  const sEl = serpRoot().querySelector<HTMLElement>('#mvStart') as HTMLInputElement, eEl = serpRoot().querySelector<HTMLElement>('#mvEnd') as HTMLInputElement;
  if (!tSel) return;
  render(html`${terms.map(t => html`<option ?selected=${t===MV_STATE.term}>${fixMojibake(t)}</option>`)}`, tSel);
  render(html`${locs.map(l => html`<option ?selected=${l===MV_STATE.location}>${l}</option>`)}`, lSel!);
  sEl.value = MV_STATE.start || ''; eEl.value = MV_STATE.end || '';
  sEl.min = eEl.min = win.start || ''; sEl.max = eEl.max = win.end || '';
  tSel.onchange = () => { MV_STATE.term = tSel.value; MV_STATE.data = null; renderTrend(state); };
  lSel!.onchange = () => { MV_STATE.location = lSel!.value; MV_STATE.data = null; mvLoad(state); };
  sEl.onchange = () => { MV_STATE.start = sEl.value; MV_STATE.data = null; mvLoad(state); };
  eEl.onchange = () => { MV_STATE.end = eEl.value; MV_STATE.data = null; mvLoad(state); };
  mvLoad(state);
}

export async function mvLoad(state: AppPayloadState): Promise<void> {
  const key = [MV_STATE.term, MV_STATE.location, MV_STATE.start, MV_STATE.end].join('|');
  if (MV_STATE.data && MV_STATE.data._key === key) return mvRender(MV_STATE.data, state);
  // FIX-audit F2: capture the render context at request start; discard the
  // post-await commit if a newer render (another client/surface) superseded it.
  const ctx = captureSerp();
  const meta = ctx.root.querySelector<HTMLElement>('#trendMeta')!;
  // render(), not textContent= — #trendMeta is also written via render() in
  // mvRender/mvRenderStatic below; render() only manages its own marker
  // node, so a plain textContent/innerHTML write interleaved with render()
  // calls on the SAME element leaves stale content behind (or corrupts the
  // next render() — it doesn't clear the container the way innerHTML= does).
  // Every write to a render()-targeted container must go through render().
  render(html`loading movement from IMPACT… (first look at a client loads the full history — up to a minute; instant after that)`, meta);
  try {
    const data: TimelineResponse & { _key?: string } = await fetchTimeline({ brand: state.brand || '', term: MV_STATE.term || '',
      location: MV_STATE.location || '', start: MV_STATE.start || '',
      end: MV_STATE.end || '', max_polls: '12' });
    if (!isSerpCurrent(ctx)) return; // superseded during the await — discard
    if (!data.available || !(data.polls||[]).length) throw new Error(data.reason || 'no polls in range');
    data._key = key;
    MV_STATE.data = data;
    mvRender(data, state);
  } catch(e){
    if (!isSerpCurrent(ctx)) return;
    mvRenderStatic(state);
  }
}

/** The per-poll SoV series (date · owned · negative · slots) from raw IMPACT
 * polls — the page-1 ownership math. Exported so the multi-term small-multiples
 * view (views/smallMultiples.ts) builds each term's series identically. */
export function pollsToSeries(polls: TimelinePoll[]): SovPoint[] {
  return polls.map((p) => {
    const rs = (p.results || []).filter((r) => r.page === 1);
    return {
      date: (p.capturedAt || '').slice(0, 10),
      owned: rs.filter((r) => r.ownership === 'owned').length,
      negative: rs.filter((r) => r.rating === 'negative').length,
      slots: Math.max(rs.length, 1),
    };
  });
}

/* everything the movement story needs, computed from the filtered polls */
function mvAnalyze(polls: TimelinePoll[]) {
  const p1 = (p: TimelinePoll | undefined) => ((p && p.results)||[]).filter((r) => r.page === 1);
  const series = pollsToSeries(polls);
  const F = polls[0], L = polls[polls.length-1];
  const fMap = new Map<string | null, SerpResult>(p1(F).map((r) => [urlNorm(r.url, {mode:'full'}), r]));
  const lMap = new Map<string | null, SerpResult>(p1(L).map((r) => [urlNorm(r.url, {mode:'full'}), r]));
  const entered: MvMove[] = [], dropped: MvMove[] = [], movers: MvMove[] = [];
  lMap.forEach((r, u) => { if (!fMap.has(u)) entered.push({...r, status:'entered', firstPos:null, lastPos:r.rank}); });
  fMap.forEach((r, u) => {
    const now = lMap.get(u);
    if (!now){
      const p2 = ((L && L.results)||[]).find((x) => urlNorm(x.url, {mode:'full'}) === u);
      dropped.push({...r, status:'dropped', firstPos:r.rank, lastPos:p2 ? p2.rank : null});
    } else if (Math.abs(now.rank - r.rank) >= 2)
      movers.push({...now, status:'moved', firstPos:r.rank, lastPos:now.rank, delta:r.rank - now.rank});
  });
  // A result whose URL string changed between polls (tracking params, Google
  // Flights dates…) shows up as one "entered" + one "dropped" — reunite those
  // pairs by domain+title and report them as a single move instead.
  const rKey = (r: MvMove) => `${domainOf(r.url)||''}|${(fixMojibake(r.title)||'').toLowerCase().trim()}`;
  let reunited = 0;
  for (let i = entered.length - 1; i >= 0; i--){
    const cur = entered[i];
    if (!cur) continue; // index always in range — loop bound; narrows for TS
    const j = dropped.findIndex(x => rKey(x) === rKey(cur));
    if (j === -1) continue;
    const from = dropped[j], to = cur;
    if (!from) continue; // findIndex hit — always defined; narrows for TS
    dropped.splice(j,1); entered.splice(i,1); reunited++;
    if (Math.abs(to.rank - from.rank) >= 2)
      movers.push({...to, status:'moved', firstPos:from.rank, lastPos:to.rank, delta:from.rank - to.rank});
  }
  // every movers row is a 'moved' push carrying delta — ?? 0 is unreachable
  movers.sort((a,b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0));
  let sticky = [...fMap.keys()];
  polls.slice(1).forEach((p) => { const s = new Set(p1(p).map((r) => urlNorm(r.url, {mode:'full'}))); sticky = sticky.filter(u => s.has(u)); });
  const held = [...lMap.keys()].filter(u => fMap.has(u)).length + reunited;
  const churnPct = lMap.size ? Math.max(Math.round((1 - held / lMap.size) * 100), 0) : 0;
  const featOf = (p: TimelinePoll | undefined) => new Set(((p && p.features)||[]).map((f) => f.type));
  const fF = featOf(F), lF = featOf(L);
  const featLabel = (t: string | undefined) => (typeof IMP_FEATURE_LABEL !== 'undefined' && t && IMP_FEATURE_LABEL[t]) || t;
  return { series, F, L, entered, dropped, movers, churnPct,
    // every surviving sticky key came from fMap, so get() always hits — the
    // filter is a TS narrowing no-op, not a data change
    sticky: sticky.map(u => fMap.get(u)).filter((r): r is SerpResult => !!r),
    negNow: [...lMap.values()].filter((r) => r.rating === 'negative'),
    negGone: [...fMap.entries()].filter(([u, r]) => r.rating === 'negative' && !lMap.has(u)).map(([, r]) => r),
    featIn: [...lF].filter(t => !fF.has(t)).map(featLabel),
    featOut: [...fF].filter(t => !lF.has(t)).map(featLabel) };
}

// CH-1 page-1 ownership % trend (renamed from "share of voice" — OWNER RULING
// line replaces the raw-owned-count trendbars — y = % of page-1 slots owned
// (owned/slots), 0–100% axis, a marker on every real poll, the Undesirable
// count as a sub-track (never a y-series), plus the mandatory provenance
// caption and the table twin. Same series the bars used; no new data.
function sovLineChart(series: SovPoint[], opts: { term?: string | null; location?: string | null }): TemplateResult {
  const points = series.map((p) => ({
    date: p.date,
    value: (p.owned || 0) / Math.max(p.slots || 0, 1),
    sub: p.negative || 0,
  }));
  const layout = percentLineLayout(points);
  const f = points[0], l = points[points.length - 1];
  // callers never pass an empty series (every path checks length first) —
  // the guard exists only so TS index reads narrow; it renders nothing on
  // the impossible empty input rather than throwing like the untyped code.
  if (!f || !l) return html``;
  const undes = points.reduce((a, p) => a + (p.sub || 0), 0);
  const dir = pct(l.value) > pct(f.value) ? 'up' : pct(l.value) < pct(f.value) ? 'down' : 'flat';
  const nDays = points.length;
  const aria = `Share of page 1 owned on ${opts.term}: ${nDays === 1 ? `${pct(l.value)}% (single capture)` : dir === 'flat' ? `held at ${pct(l.value)}%` : `${dir} from ${pct(f.value)}% to ${pct(l.value)}%`} over ${nDays} polled ${nDays === 1 ? 'day' : 'days'}${undes ? `; ${undes} Undesirable result${undes > 1 ? 's' : ''} in the window` : ''}.`;
  // §3.1: "across N polled days" makes the index-spacing honest and survives
  // crop-to-deck; the window dates keep the calendar span.
  const cap = `Page-1 ownership on “${fixMojibake(opts.term)}”${opts.location ? ` in ${opts.location}` : ''} across ${nDays} polled ${nDays === 1 ? 'day' : 'days'} · IMPACT · ${f.date} → ${l.date}`;
  // §3.2: a polling hole is a gap, never an interpolated segment — count the
  // holes under the chart (the line already breaks across them).
  const holesText = layout.holes.length
    ? layout.holes.map((h) => `no capture ${h.from.slice(5)} → ${h.to.slice(5)}`).join(' · ')
    : '';
  const holeNote = holesText
    ? html`<div class="chart-cap chart-holes">${holesText}</div>`
    : nothing;
  const tableCols = ['Date', '% owned', 'Owned / slots', 'Undesirable'];
  const tableRows = series.map((p) => [p.date, `${pct((p.owned || 0) / Math.max(p.slots || 0, 1))}%`, `${p.owned}/${p.slots}`, p.negative || 0]);
  const title = `Page-1 ownership — ${fixMojibake(opts.term)}${opts.location ? ` · ${opts.location}` : ''}`;
  // .chartwrap lets the export toolbar locate this chart's svg; the export bakes
  // the title + caption into the artifact (provenance travels to the deck).
  // P2-3: the gap footnote (holesText) is passed to the export so the exported
  // SVG/PNG keeps the precise "no capture X→Y" dates, not just the on-screen div.
  return html`<div class="chartwrap">${percentLineSvg(layout, aria)}${chartCaption(cap)}${holeNote}${chartExportBar({ title, caption: cap, footnote: holesText, tableCols, tableRows })}${chartTableTwin(tableCols, tableRows)}</div>`;
}

export function mvRender(d: TimelineResponse, state: AppPayloadState): void {
  const meta = serpRoot().querySelector<HTMLElement>('#trendMeta')!;
  const chart = serpRoot().querySelector<HTMLElement>('#trendChart')!;
  const takeaways = serpRoot().querySelector<HTMLElement>('#trendTakeaways')!;
  const movers = serpRoot().querySelector<HTMLElement>('#moversList')!;
  const polls = d.polls || [];
  if (!polls.length){
    render(html`No captures in this window — widen the dates to see results.`, meta);
    render(html``, chart); render(html``, takeaways); render(html``, movers); return;
  }
  const a = mvAnalyze(polls);
  const f = a.series[0], l = a.series[a.series.length-1];
  if (!f || !l) return; // polls.length checked above — series is 1:1 with polls
  // A single capture still plots its one real marker (never a fabricated flat
  // line) — the copy explains there's no movement yet; takeaways/movers need a
  // second poll to compute.
  if (polls.length < 2){
    render(html`Only one capture in this window — widen the dates to see movement.`, meta);
    render(sovLineChart(a.series, { term: d.term, location: d.location }), chart);
    render(html``, takeaways); render(html``, movers); return;
  }
  const delta = l.owned - f.owned;
  render(html`page-1 results owned on “${fixMojibake(d.term)}” in ${d.location||'—'} — ${
    delta>0?`up ${delta} since ${f.date}`:delta<0?`down ${Math.abs(delta)} since ${f.date}`:`flat since ${f.date}`
    } · ${d.totalPollDays||polls.length} polled days in range`, meta);

  render(sovLineChart(a.series, { term: d.term, location: d.location }), chart);

  const domsOf = (list: SerpResult[]) => [...new Set(list.map((r) => domainOf(r.url)).filter(Boolean))];
  const nameList = (list: SerpResult[]) => domsOf(list).slice(0,3).join(', ') + (domsOf(list).length>3?` +${domsOf(list).length-3}`:'');
  const t: { cls: string; tpl: TemplateResult }[] = [];
  t.push({ cls: delta>0?'good':delta<0?'warn':'', tpl:
    delta === 0 ? html`Owned presence held steady at <b>${l.owned}/${l.slots}</b> page-1 results across the window.`
    : html`Owned presence ${delta>0?'grew':'fell'} from <b>${f.owned}/${f.slots}</b> to <b>${l.owned}/${l.slots}</b> page-1 results.`});
  if (a.negNow.length)
    t.push({ cls:'warn', tpl:html`<b>${a.negNow.length} Undesirable</b> on page 1 now: ${nameList(a.negNow)} (${a.negNow.map((r)=>'#'+r.rank).join(', ')}).`});
  else if (a.negGone.length)
    t.push({ cls:'good', tpl:html`All Undesirable results left page 1 this window (${nameList(a.negGone)}) — worth monitoring, they can return.`});
  else
    t.push({ cls:'good', tpl:html`No Undesirable result reached page 1 in this window.`});
  t.push({ cls:'', tpl:html`<b>${a.churnPct}% of page-1 slots changed hands</b> since ${f.date} — ${
    a.churnPct >= 40 ? 'a volatile SERP: movement is achievable here with fresh, authoritative content'
    : a.churnPct >= 15 ? 'moderate churn: targeted pushes can win slots'
    : 'a sticky SERP: displacing what ranks will take sustained work'}.`});
  if (a.sticky.length)
    t.push({ cls:'', tpl:html`The stable core: <b>${a.sticky.length} results never left page 1</b> (${nameList(a.sticky)}) — these define the search picture the client lives with.`});
  if (a.entered.length || a.dropped.length)
    t.push({ cls:'', tpl:html`${a.entered.length ? html`<b>${a.entered.length} new to page 1</b> (${nameList(a.entered)})` : ''}${a.entered.length && a.dropped.length ? '; ' : ''}${a.dropped.length ? html`<b>${a.dropped.length} dropped out</b> (${nameList(a.dropped)})` : ''}.`});
  if (a.featIn.length || a.featOut.length)
    t.push({ cls:'', tpl:html`Google changed the page furniture: ${a.featIn.length?`added ${a.featIn.join(', ')}`:''}${a.featIn.length&&a.featOut.length?'; ':''}${a.featOut.length?`removed ${a.featOut.join(', ')}`:''}.`});
  render(html`${t.slice(0,6).map(x => html`<li class="${x.cls}">${x.tpl}</li>`)}`, takeaways);

  const mv = [...a.entered, ...a.dropped, ...a.movers].slice(0,6);
  render(html`${mv.length ? html`<div class="sub" style="margin:12px 0 4px;font-size:12px;color:var(--muted)">biggest movers in this window</div>` : nothing}
    ${mv.map((m) => {
      const up = m.status === 'entered' || (m.delta||0) > 0;
      const label = m.status==='entered' ? 'entered page 1' : m.status==='dropped' ? 'left page 1' : ((m.delta||0)>0?`▲ +${m.delta}`:`▼ ${m.delta}`);
      const posTxt = m.status==='entered' ? `→ #${m.lastPos}` : m.lastPos!=null ? `#${m.firstPos}→#${m.lastPos}` : `#${m.firstPos}→ off`;
      return html`<div class="dombar mv-r" style="cursor:pointer" @click=${(ev: MouseEvent) => openResultPopover(state, m.url||'', ev, { term: d.term, location: d.location })}><img src="${faviconUrl(m.url,32)}" alt="">
        <div class="dm" style="flex:1"><div class="d">${fixMojibake(m.title)||domainOf(m.url)||''}</div><div class="s">${domainOf(m.url)||''}</div></div>
        <div class="val" style="color:${up?'var(--green-tx)':'var(--neg)'}">${label} <span style="color:var(--muted)">${posTxt}</span></div></div>`;
    })}`, movers);
}

/* offline fallback: the run's baked primary-term digest (no filtering) */
export function mvRenderStatic(state: AppPayloadState): void {
  const tr = state.impact && state.impact.trends;
  const meta = serpRoot().querySelector<HTMLElement>('#trendMeta')!;
  const chart = serpRoot().querySelector<HTMLElement>('#trendChart')!;
  const takeaways = serpRoot().querySelector<HTMLElement>('#trendTakeaways')!;
  const movers = serpRoot().querySelector<HTMLElement>('#moversList')!;
  if (!tr || !tr.ownershipTimeline || !tr.ownershipTimeline.length){
    render(html`Not enough polling history yet — a second run over time enables the month-over-month story.`, meta);
    render(html``, chart); render(html``, takeaways); render(html``, movers); return;
  }
  const filtersMatch = MV_STATE.term === tr.primaryTerm && MV_STATE.location === tr.location;
  const tl = tr.ownershipTimeline;
  // A single baked capture plots its one marker; the copy flags there's no
  // history yet rather than bailing to text (the tested geometry renders).
  if (tl.length < 2){
    render(html`page-1 results owned on “${fixMojibake(tr.primaryTerm)}” in ${tr.location} — one capture so far <span style="color:var(--neg)">· service not reachable on :8000 — a second run over time enables the trend</span>`, meta);
    render(sovLineChart(tl, { term: tr.primaryTerm, location: tr.location }), chart);
    render(html``, takeaways); render(html``, movers); return;
  }
  const f = tl[0], l = tl[tl.length-1];
  if (!f || !l) return; // tl.length >= 2 checked above — narrows for TS
  const d = l.owned - f.owned;
  render(html`page-1 results owned on “${fixMojibake(tr.primaryTerm)}” in ${tr.location} — ${d>0?`up ${d} since ${f.date}`:d<0?`down ${Math.abs(d)} since ${f.date}`:`flat since ${f.date}`}
    <span style="color:var(--neg)">· service not reachable on :8000 — showing the run's baked digest${filtersMatch?'':' (filters need the live service)'}</span>`, meta);
  render(sovLineChart(tl, { term: tr.primaryTerm, location: tr.location }), chart);
  render(html``, takeaways);
  const mv = (tr.movements||[]).slice(0,5);
  render(html`${mv.length ? html`<div class="sub" style="margin:12px 0 4px;font-size:12px;color:var(--muted)">biggest movers on this term</div>` : nothing}
    ${mv.map((m) => {
      const up = (m.delta||0) > 0 || m.status === 'entered';
      const label = m.status==='entered' ? 'entered page 1' : m.status==='dropped' ? 'left page 1' : ((m.delta||0)>0?`▲ +${m.delta}`:`▼ ${m.delta}`);
      return html`<div class="dombar"><img src="${faviconUrl(m.url,32)}" alt="">
        <div class="dm" style="flex:1"><div class="d">${fixMojibake(m.title)||m.domain}</div><div class="s">${m.domain||domainOf(m.url)||''}</div></div>
        <div class="val" style="color:${up?'var(--green-tx)':'var(--neg)'}">${label} <span style="color:var(--muted)">#${m.firstPos}→#${m.lastPos}</span></div></div>`;
    })}`, movers);
}
